/**
 * Image Index — cross-dealer inventory + photo coverage. Top: dealer-store
 * filter dropdown plus a has-photos / no-photos / all toggle. Below:
 * sortable table of capture sessions, one row per (dealer, vin), with
 * the row click navigating to /photos/vehicles/:vin?dealer=:slug.
 *
 * Filter state is mirrored into URL search params (`dealer`, `has_photos`)
 * so the page is shareable and survives reload — see frontend-standards
 * SKILL.md §7 "URL is shared state."
 */
import React, { useEffect, useMemo, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"

import Layout from "../../components/Layout"
import SearchInput from "../../components/audit/SearchInput"
import AddVehicleModal from "../../components/photos/AddVehicleModal"
import DealerGroupRequiredNotice from "../../components/DealerGroupRequiredNotice"
import ExportStatusChip from "../../components/photos/ExportStatusChip"
import InFeedStatusChip from "../../components/photos/InFeedStatusChip"
import { useDealerGroup } from "../../context/DealerGroupContext"
import { THEME } from "../../config/theme"
import { tint } from "../../utils/color"
import {
  RooftopOption,
  ImageIndexVehicle,
  listRooftops,
  listVehicles,
} from "../../api/imageIndex"

type HasPhotosFilter = "all" | "yes" | "no"

const HAS_PHOTOS_OPTIONS: { value: HasPhotosFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "yes", label: "Has photos" },
  { value: "no",  label: "Missing" },
]

const ALL_ROOFTOPS_VALUE = "__all__"

const MOBILE_BREAKPOINT = 768

export default function ImageIndexPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  // It's a tool: require a concrete dealer group; rooftops + vehicles are scoped
  // to it, and the list re-fetches when the active group changes.
  const { dealerGroupId } = useDealerGroup()

  const isMobile =
    typeof window !== "undefined" && window.innerWidth < MOBILE_BREAKPOINT

  const rooftopSlug = searchParams.get("rooftop") ?? ALL_ROOFTOPS_VALUE
  const hasPhotos = (searchParams.get("has_photos") as HasPhotosFilter | null) ?? "all"
  const query = searchParams.get("q") ?? ""

  const [rooftops, setRooftops] = useState<RooftopOption[]>([])
  const [vehicles, setVehicles] = useState<ImageIndexVehicle[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [addModalOpen, setAddModalOpen] = useState<boolean>(false)
  // Bumped by Add Vehicle to force the vehicles list to re-fetch.
  const [refreshTick, setRefreshTick] = useState<number>(0)

  // Dealer dropdown reloads when the active group changes (scoped to it).
  useEffect(() => {
    if (dealerGroupId === null) {
      setRooftops([])
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const data = await listRooftops(dealerGroupId)
        if (!cancelled) setRooftops(data)
      } catch {
        // Filter dropdown is non-critical — fall back to All-rooftops if the
        // rooftops fetch fails. The vehicles fetch will surface its own error.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [dealerGroupId])

  // Vehicles re-fetch whenever the filter state or the active group changes.
  useEffect(() => {
    if (dealerGroupId === null) {
      setVehicles([])
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    ;(async () => {
      try {
        const params: Parameters<typeof listVehicles>[0] = { dealerGroupId }
        if (rooftopSlug !== ALL_ROOFTOPS_VALUE) params.rooftop = rooftopSlug
        if (hasPhotos === "yes") params.hasPhotos = true
        if (hasPhotos === "no")  params.hasPhotos = false
        const data = await listVehicles(params)
        if (!cancelled) setVehicles(data)
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load vehicles")
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [rooftopSlug, hasPhotos, refreshTick, dealerGroupId])

  const handleDealerChange = (value: string) => {
    const next = new URLSearchParams(searchParams)
    if (value === ALL_ROOFTOPS_VALUE) next.delete("dealer")
    else next.set("dealer", value)
    setSearchParams(next, { replace: true })
  }

  const handleHasPhotosChange = (value: HasPhotosFilter) => {
    const next = new URLSearchParams(searchParams)
    if (value === "all") next.delete("has_photos")
    else next.set("has_photos", value)
    setSearchParams(next, { replace: true })
  }

  const handleSearchChange = (next: string) => {
    const updated = new URLSearchParams(searchParams)
    if (next.trim()) updated.set("q", next)
    else updated.delete("q")
    setSearchParams(updated, { replace: true })
  }

  const dealerOptions = useMemo(
    () => [{ slug: ALL_ROOFTOPS_VALUE, name: "All rooftops" }, ...rooftops],
    [rooftops],
  )

  // Client-side search across the same columns visible in the table:
  // VIN, stock, year, make, model, and dealer name. Trade-off: limited
  // by the backend's `limit` (currently 100). When the dataset grows
  // past that, swap to a backend `?q=` param on /image-index/vehicles.
  const filteredVehicles = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return vehicles
    return vehicles.filter((v) => {
      const haystack = [
        v.vin,
        v.stock ?? "",
        v.year !== null ? String(v.year) : "",
        v.make ?? "",
        v.model ?? "",
        v.rooftop_name,
      ].join(" ").toLowerCase()
      return haystack.includes(q)
    })
  }, [vehicles, query])

  // Working surface: under "All Groups" require a concrete group first.
  if (dealerGroupId === null) {
    return (
      <Layout>
        <div style={containerStyle}>
          <h1 style={{ ...titleStyle, marginBottom: THEME.spacing.md }}>Image Index</h1>
          <DealerGroupRequiredNotice surface="image index" />
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div
        style={{
          ...containerStyle,
          ...(isMobile && { padding: THEME.spacing.md }),
        }}
      >
        <div
          style={{
            ...headerRowStyle,
            ...(isMobile && {
              flexDirection: "column",
              alignItems: "stretch",
              gap: THEME.spacing.sm,
            }),
          }}
        >
          <div>
            <h1 style={titleStyle}>Image Index</h1>
            <p style={subtitleStyle}>
              Photo coverage across the dealer group.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setAddModalOpen(true)}
            style={{
              ...addButtonStyle,
              ...(isMobile && { width: "100%", justifyContent: "center" }),
            }}
          >
            + Add Vehicle
          </button>
        </div>

        <div style={filterRowStyle}>
          <label style={{ ...fieldStyle, ...(isMobile && { width: "100%" }) }}>
            <span style={fieldLabelStyle}>Dealer</span>
            <select
              value={rooftopSlug}
              onChange={(e) => handleDealerChange(e.target.value)}
              style={{
                ...selectStyle,
                ...(isMobile && { minWidth: 0, width: "100%" }),
              }}
            >
              {dealerOptions.map((d) => (
                <option key={d.slug} value={d.slug}>
                  {d.name}
                </option>
              ))}
            </select>
          </label>

          <div style={fieldStyle}>
            <span style={fieldLabelStyle}>Photos</span>
            <div style={segmentRowStyle}>
              {HAS_PHOTOS_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handleHasPhotosChange(opt.value)}
                  style={segmentButtonStyle(opt.value === hasPhotos)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div style={searchRowStyle}>
          <span style={fieldLabelStyle}>Search</span>
          <SearchInput
            value={query}
            onChange={handleSearchChange}
            placeholder="VIN, stock, or dealer…"
          />
        </div>

        {error && <div style={errorBannerStyle}>Failed to load: {error}</div>}

        {isMobile ? (
          <VehicleCardList
            vehicles={filteredVehicles}
            loading={loading}
            onCardClick={(v) => navigate(rowDetailPath(v))}
          />
        ) : (
          <VehicleTable
            vehicles={filteredVehicles}
            loading={loading}
            onRowClick={(v) => navigate(rowDetailPath(v))}
          />
        )}
      </div>
      {addModalOpen && (
        <AddVehicleModal
          rooftops={rooftops}
          defaultRooftopSlug={
            rooftopSlug !== ALL_ROOFTOPS_VALUE ? rooftopSlug : null
          }
          onClose={() => setAddModalOpen(false)}
          onCreated={(routeKey, slug) => {
            setAddModalOpen(false)
            // Drop the operator on the matching Vehicle Detail page so
            // they can Upload Images for the row they just created.
            // VIN'd rows use /photos/vehicles/:vin, stock-only rows use
            // /photos/items/:stock.
            const path =
              routeKey.kind === "vin"
                ? `/photos/vehicles/${encodeURIComponent(routeKey.value)}?dealer=${encodeURIComponent(slug)}`
                : `/photos/items/${encodeURIComponent(routeKey.value)}?dealer=${encodeURIComponent(slug)}`
            navigate(path)
            setRefreshTick((tick) => tick + 1)
          }}
        />
      )}
    </Layout>
  )
}

/**
 * Choose the right detail URL for a row. VIN'd vehicles use the
 * /photos/vehicles/:vin route; stock-only items (CTA campers etc.) use
 * the parallel /photos/items/:stock route. If neither is present the
 * row falls back to the session-id endpoint — that should never happen
 * since the backend rejects sessions with no identifier, but the
 * fallback keeps the click handler total.
 */
function rowDetailPath(v: ImageIndexVehicle): string {
  if (v.vin) {
    return `/photos/vehicles/${encodeURIComponent(v.vin)}?dealer=${encodeURIComponent(v.rooftop_slug)}`
  }
  if (v.stock) {
    return `/photos/items/${encodeURIComponent(v.stock)}?dealer=${encodeURIComponent(v.rooftop_slug)}`
  }
  return "#"
}

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

interface VehicleTableProps {
  vehicles: ImageIndexVehicle[]
  loading: boolean
  onRowClick: (v: ImageIndexVehicle) => void
}

function VehicleTable({ vehicles, loading, onRowClick }: VehicleTableProps) {
  if (loading) {
    return <div style={emptyStateStyle}>Loading…</div>
  }
  if (vehicles.length === 0) {
    return <div style={emptyStateStyle}>No vehicles match these filters.</div>
  }
  return (
    <div style={tableWrapperStyle}>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={headerCellStyle}>Dealer</th>
            <th style={headerCellStyle}>VIN</th>
            <th style={headerCellStyle}>Stock</th>
            <th style={headerCellStyle}>Year</th>
            <th style={headerCellStyle}>Make</th>
            <th style={headerCellStyle}>Model</th>
            <th style={headerCellStyle}>Feed</th>
            <th style={{ ...headerCellStyle, textAlign: "center" }}>Photos</th>
            <th style={{ ...headerCellStyle, textAlign: "center" }}>#</th>
            <th style={headerCellStyle}>Last Captured</th>
            <th style={headerCellStyle}>Export</th>
          </tr>
        </thead>
        <tbody>
          {vehicles.map((v, i) => (
            <tr
              key={v.session_id}
              onClick={() => onRowClick(v)}
              style={rowStyle(i)}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = THEME.auditTable.rowHoverBackground
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background =
                  i % 2 === 0
                    ? THEME.auditTable.zebraLight
                    : THEME.auditTable.zebraMain
              }}
            >
              <td style={dataCellStyle}>{v.rooftop_name}</td>
              <td style={{ ...dataCellStyle, fontFamily: monoFont }}>
                {v.vin ?? (
                  <span style={noVinHintStyle}>No VIN</span>
                )}
              </td>
              <td style={dataCellStyle}>{v.stock ?? "—"}</td>
              <td style={dataCellStyle}>{v.year ?? "—"}</td>
              <td style={dataCellStyle}>{v.make ?? "—"}</td>
              <td style={dataCellStyle}>{v.model ?? "—"}</td>
              <td style={dataCellStyle}>
                <InFeedStatusChip status={v.in_feed_status} />
              </td>
              <td style={{ ...dataCellStyle, textAlign: "center" }}>
                <PhotoStatusChip count={v.photo_count} />
              </td>
              <td style={{ ...dataCellStyle, textAlign: "center" }}>
                {v.photo_count.toLocaleString()}
              </td>
              <td style={dataCellStyle}>{formatDate(v.captured_at)}</td>
              <td style={dataCellStyle}>
                <ExportStatusChip status={v.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Card list (mobile)
// ---------------------------------------------------------------------------

interface VehicleCardListProps {
  vehicles: ImageIndexVehicle[]
  loading: boolean
  onCardClick: (v: ImageIndexVehicle) => void
}

function VehicleCardList({ vehicles, loading, onCardClick }: VehicleCardListProps) {
  if (loading) {
    return <div style={emptyStateStyle}>Loading…</div>
  }
  if (vehicles.length === 0) {
    return <div style={emptyStateStyle}>No vehicles match these filters.</div>
  }
  return (
    <div style={cardListStyle}>
      {vehicles.map((v) => (
        <div
          key={v.session_id}
          onClick={() => onCardClick(v)}
          style={cardStyle}
        >
          <div style={cardTitleStyle}>{vehicleTitle(v)}</div>
          <div style={cardIdentifierRowStyle}>
            {v.vin ? (
              <span style={{ fontFamily: monoFont }}>{v.vin}</span>
            ) : v.stock ? (
              <span>Stock: {v.stock}</span>
            ) : (
              <span style={noVinHintStyle}>No VIN</span>
            )}
          </div>
          <div style={cardDealerStyle}>{v.rooftop_name}</div>
          <div style={cardFooterStyle}>
            <PhotoStatusChip count={v.photo_count} />
            {v.photo_count > 0 && (
              <span style={cardMetaStyle}>
                {v.photo_count} photo{v.photo_count !== 1 ? "s" : ""}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

function vehicleTitle(v: ImageIndexVehicle): string {
  const parts: Array<string | number> = []
  if (v.year !== null && v.year !== undefined) parts.push(v.year)
  if (v.make) parts.push(v.make)
  if (v.model) parts.push(v.model)
  if (parts.length > 0) return parts.join(" ")
  return v.vin ?? v.stock ?? "Vehicle"
}

// ---------------------------------------------------------------------------
// Status chips
// ---------------------------------------------------------------------------

function PhotoStatusChip({ count }: { count: number }) {
  const hasPhotos = count > 0
  const accent = hasPhotos
    ? THEME.auditTable.tabColors.completed
    : THEME.auditTable.tabColors.discrepancies
  return (
    <span style={chipStyle(accent)}>
      {hasPhotos ? "✓ Has photos" : "✗ Missing"}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

const monoFont = 'ui-monospace, "SF Mono", Menlo, Consolas, monospace'

// SQLite returns DateTime(timezone=True) values as naive ISO strings on read
// (see backend patterns.md "SQLite gotcha — naive readback"). The backend
// values are UTC; we treat naive strings as UTC explicitly so the displayed
// time doesn't shift based on the operator's machine clock interpretation.
function formatDate(iso: string | null): string {
  if (!iso) return "—"
  const withTz = /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`
  const d = new Date(withTz)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const containerStyle: React.CSSProperties = {
  padding: THEME.spacing.lg,
  fontFamily: THEME.fonts.primary,
}

const headerRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  marginBottom: THEME.spacing.md,
}

const titleStyle: React.CSSProperties = {
  fontSize: 24,
  fontWeight: 500,
  letterSpacing: "-0.5px",
  color: THEME.colors.neutral.light.textPrimary,
  margin: 0,
}

const subtitleStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.neutral.light.textSecondary,
  marginTop: THEME.spacing.xs,
}

const filterRowStyle: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: THEME.spacing.lg,
  alignItems: "flex-end",
  marginBottom: THEME.spacing.sm,
}

const searchRowStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: THEME.spacing.xs,
  marginBottom: THEME.spacing.md,
}

const fieldStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: THEME.spacing.xs,
}

const fieldLabelStyle: React.CSSProperties = {
  fontSize: THEME.typography.caption.size,
  fontWeight: THEME.typography.caption.weight,
  letterSpacing: THEME.typography.caption.tracking,
  textTransform: "uppercase",
  color: THEME.colors.neutral.light.textSecondary,
}

const selectStyle: React.CSSProperties = {
  fontSize: 13,
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  background: THEME.colors.neutral.light.surface,
  color: THEME.colors.neutral.light.textPrimary,
  fontFamily: THEME.fonts.primary,
  minWidth: 200,
  cursor: "pointer",
}

const segmentRowStyle: React.CSSProperties = {
  display: "flex",
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  overflow: "hidden",
}

const segmentButtonStyle = (active: boolean): React.CSSProperties => ({
  fontSize: 13,
  padding: `${THEME.spacing.xs} ${THEME.spacing.md}`,
  border: "none",
  borderRight: `1px solid ${THEME.colors.silver}`,
  background: active ? THEME.colors.navy : THEME.colors.neutral.light.surface,
  color: active ? THEME.colors.white : THEME.colors.neutral.light.textPrimary,
  fontWeight: active ? 600 : 500,
  fontFamily: THEME.fonts.primary,
  cursor: "pointer",
})

// Matches the Upload Images / Repush button on Vehicle Detail
// (repushButtonStyle there) so the Add Vehicle CTA reads as a primary
// action of the same weight.
// Italic muted label for stock-only rows whose VIN cell is empty by
// design (campers, trailers identified only by an internal stock #).
const noVinHintStyle: React.CSSProperties = {
  fontStyle: "italic",
  color: THEME.colors.neutral.light.textSecondary,
  fontFamily: THEME.fonts.primary,
}

const addButtonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: THEME.spacing.xs,
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  fontSize: 14,
  fontWeight: 500,
  borderRadius: THEME.borderRadius.md,
  border: `1px solid ${THEME.colors.navy}`,
  background: THEME.colors.navy,
  color: THEME.colors.white,
  fontFamily: THEME.fonts.primary,
  cursor: "pointer",
  whiteSpace: "nowrap",
}

const errorBannerStyle: React.CSSProperties = {
  background: tint(THEME.colors.semantic.danger, 0.1),
  border: `1px solid ${THEME.colors.semantic.danger}`,
  borderRadius: THEME.borderRadius.md,
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  color: THEME.colors.semantic.danger,
  fontSize: 13,
  marginBottom: THEME.spacing.md,
}

const tableWrapperStyle: React.CSSProperties = {
  border: `1px solid ${THEME.auditTable.borderColor}`,
  borderRadius: THEME.borderRadius.md,
  overflow: "hidden",
  background: THEME.colors.neutral.light.surface,
}

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontFamily: THEME.fonts.primary,
}

const headerCellStyle: React.CSSProperties = {
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
  fontSize: 12,
  fontWeight: 500,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  color: THEME.colors.white,
  textAlign: "left",
  backgroundColor: THEME.auditTable.headerBackground,
  borderRight: "1px solid rgba(255, 255, 255, 0.1)",
  borderBottom: `1px solid ${THEME.auditTable.borderColor}`,
}

const dataCellStyle: React.CSSProperties = {
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
  fontSize: 12,
  color: THEME.colors.neutral.light.textPrimary,
  borderRight: `1px solid ${THEME.auditTable.borderColor}`,
  borderBottom: `1px solid ${THEME.auditTable.borderColor}`,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
}

const rowStyle = (i: number): React.CSSProperties => ({
  background:
    i % 2 === 0 ? THEME.auditTable.zebraLight : THEME.auditTable.zebraMain,
  cursor: "pointer",
})

const chipStyle = (accent: string): React.CSSProperties => ({
  display: "inline-block",
  padding: "2px 8px",
  fontSize: 11,
  fontWeight: 500,
  borderRadius: 999,
  background: tint(accent, 0.5),
  color: THEME.colors.neutral.light.textPrimary,
  border: `1px solid ${accent}`,
  textTransform: "lowercase",
})

const emptyStateStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.neutral.light.textSecondary,
  padding: THEME.spacing.lg,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  background: THEME.colors.neutral.light.surface,
  textAlign: "center",
}

const cardListStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: THEME.spacing.sm,
}

const cardStyle: React.CSSProperties = {
  background: THEME.colors.neutral.light.surface,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  padding: THEME.spacing.md,
  fontFamily: THEME.fonts.primary,
  cursor: "pointer",
  WebkitTapHighlightColor: "rgba(0,0,0,0.05)",
}

const cardTitleStyle: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 600,
  color: THEME.colors.neutral.light.textPrimary,
  marginBottom: THEME.spacing.xs,
}

const cardIdentifierRowStyle: React.CSSProperties = {
  fontSize: 12,
  color: THEME.colors.neutral.light.textSecondary,
  marginBottom: 2,
}

const cardDealerStyle: React.CSSProperties = {
  fontSize: 12,
  color: THEME.colors.neutral.light.textSecondary,
  marginBottom: THEME.spacing.sm,
}

const cardFooterStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: THEME.spacing.sm,
}

const cardMetaStyle: React.CSSProperties = {
  fontSize: 11,
  color: THEME.colors.neutral.light.textSecondary,
}
